/**
 * Deterministic, seedable PRNG (mulberry32).
 *
 * The simulation must NOT call Math.random() directly: a seeded RNG keeps the
 * sim reproducible (identical inputs + seed => identical outcome), which is
 * what makes it testable headless and replayable/portable to a server.
 */
export class Rng {
  private state: number;

  constructor(seed: number) {
    // Ensure a non-zero 32-bit state.
    this.state = seed >>> 0 || 0x9e3779b9;
  }

  /** Next float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = Math.imul(this.state ^ (this.state >>> 15), 1 | this.state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** Random unit-length direction as { x, y }. */
  direction(): { x: number; y: number } {
    const angle = this.next() * Math.PI * 2;
    return { x: Math.cos(angle), y: Math.sin(angle) };
  }
}
